import { useMutation } from "@apollo/client/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { constantsData, identifiersData, namesData, statusTypesData } from "@/data";
import { deleteReleaseClientLinkMutation, getReleaseClientLinksListQuery } from "@/graph";
import { openAlert } from "@/redux/actions";
import { displayErrorHandler } from "@/utils";

const useDeleteReleaseClientLink = (limit, page, releaseId) => {
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [deleteReleaseClientLinkAction, { loading }] = useMutation(deleteReleaseClientLinkMutation, {
        awaitRefetchQueries: true,
        refetchQueries: [
            {
                query: getReleaseClientLinksListQuery,
                variables: {
                    limit,
                    page,
                    releaseId,
                },
            },
        ],
    });

    const { deleteReleaseClientLinkPending: deleteReleaseClientLinkPendingToastName } = namesData.toasts;

    const { toast: toastId } = identifiersData.ids;

    const deleteReleaseClientLink = async (id) => {
        try {
            await deleteReleaseClientLinkAction({
                onCompleted: () => {
                    dispatch(
                        openAlert(
                            t("messages.deleteMsgs.success", {
                                entity: `${t("releases.entity")} ${t("labels.clientLink")}`,
                            }),
                            statusTypesData.success,
                        ),
                    );
                },
                variables: {
                    where: { id },
                },
            });
        } catch (err) {
            displayErrorHandler(
                err.message === constantsData.failedToFetch ? t("messages.networkFailure") : err.message,
                dispatch,
                openAlert,
            );
        } finally {
            toast.dismiss(`${deleteReleaseClientLinkPendingToastName}_${toastId}`);
        }
    };

    useEffect(
        () => {
            if (loading) {
                toast.info(
                    () =>
                        t("messages.deleteMsgs.pending", {
                            entity: `${t("releases.entity")} ${t("labels.clientLink")}`,
                        }),
                    { toastId: `${deleteReleaseClientLinkPendingToastName}_${toastId}` },
                );
            }
        },
        [loading], //eslint-disable-line
    );

    return { deleteReleaseClientLink };
};

export default useDeleteReleaseClientLink;
